import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { updateProfile, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaMapMarkerAlt,
  FaPhone,
  FaSave,
  FaEye,
  FaEyeSlash,
  FaExclamationTriangle
} from 'react-icons/fa';
import Navbar from '../layout/Navbar';
import '../../styles/dashboard.css';

const Settings = () => {
  const { user, userData, updateUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (userData) {
      setFormData({
        fullName: userData.fullName || '',
        email: user.email || '',
        phone: userData.phone || '',
        location: userData.location || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  }, [userData, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return false;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }

    if (formData.newPassword && formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return false;
    }

    return true;
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, {
        displayName: formData.fullName
      });

      // Update email if changed
      if (formData.email !== user.email) {
        await updateEmail(auth.currentUser, formData.email);
      }

      // Update password if provided
      if (formData.newPassword) {
        // Re-authenticate user first
        const credential = EmailAuthProvider.credential(
          user.email,
          formData.currentPassword
        );
        await reauthenticateWithCredential(user, credential);

        await updatePassword(auth.currentUser, formData.newPassword);
      }

      // Update Firestore user data
      const updatedData = {
        fullName: formData.fullName,
        phone: formData.phone,
        location: formData.location,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'users', user.uid), updatedData);

      // Update local user data
      updateUserData(updatedData);

      setMessage('Profile updated successfully!');

      // Clear password fields
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));

    } catch (error) {
      console.error('Update error:', error);
      switch (error.code) {
        case 'auth/requires-recent-login':
          setError('Please log in again to update your password');
          break;
        case 'auth/wrong-password':
          setError('Current password is incorrect');
          break;
        case 'auth/email-already-in-use':
          setError('This email is already in use');
          break;
        default:
          setError('Failed to update profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="dashboard-root">
      <div className="dashboard-container">
        <div className="dashboard-sidebar">
          <div className="user-profile-card">
            <div className="user-avatar">
              {userData?.fullName?.charAt(0) || 'U'}
            </div>
            <div className="user-info">
              <h3>{userData?.fullName || 'User'}</h3>
              <p className="user-role">{userData?.userType?.toUpperCase()}</p>
              <p className="user-location">{userData?.location}</p>
            </div>
          </div>

          <nav className="dashboard-nav">
            <a href="/dashboard" className="nav-item">
              <FaUser /> Overview
            </a>
            {userData?.userType === 'student' ? (
              <>
                <a href="/dashboard/bookings" className="nav-item">
                  <FaUser /> My Bookings
                </a>
                <a href="/dashboard/tutors" className="nav-item">
                  <FaUser /> My Tutors
                </a>
              </>
            ) : (
              <>
                <a href="/dashboard/schedule" className="nav-item">
                  <FaUser /> My Schedule
                </a>
                <a href="/dashboard/students" className="nav-item">
                  <FaUser /> My Students
                </a>
                <a href="/dashboard/earnings" className="nav-item">
                  <FaUser /> Earnings
                </a>
              </>
            )}
            <a href="/dashboard/reviews" className="nav-item">
              <FaUser /> Reviews
            </a>
            <a href="/dashboard/messages" className="nav-item">
              <FaUser /> Messages
            </a>
            <a href="/dashboard/settings" className="nav-item active">
              <FaUser /> Settings
            </a>
          </nav>
        </div>

        <div className="dashboard-main">
          <div className="dashboard-header">
            <h1>Account Settings</h1>
            <p>Manage your account information and preferences</p>
          </div>

          {error && (
            <div className="error-message">
              <FaExclamationTriangle /> {error}
            </div>
          )}

          {message && (
            <div className="success-message">
              <FaSave /> {message}
            </div>
          )}

          <form onSubmit={handleUpdateProfile} className="settings-form">
            {/* Personal Information */}
            <div className="settings-section">
              <h2>Personal Information</h2>

              <div className="form-group">
                <label>
                  <FaUser /> Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  <FaEnvelope /> Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  <FaPhone /> Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+237 6XX XXX XXX"
                />
              </div>

              <div className="form-group">
                <label>
                  <FaMapMarkerAlt /> Location
                </label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                >
                  <option value="">Select your city</option>
                  <option value="Yaoundé">Yaoundé</option>
                  <option value="Douala">Douala</option>
                  <option value="Bamenda">Bamenda</option>
                  <option value="Buea">Buea</option>
                  <option value="Limbe">Limbe</option>
                  <option value="Bafoussam">Bafoussam</option>
                  <option value="Garoua">Garoua</option>
                  <option value="Maroua">Maroua</option>
                  <option value="Ngaoundéré">Ngaoundéré</option>
                  <option value="Kumba">Kumba</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Password Change */}
            <div className="settings-section">
              <h2>Change Password</h2>
              <p className="section-note">Leave blank if you don't want to change your password</p>

              <div className="form-group">
                <label>
                  <FaLock /> Current Password
                </label>
                <div className="password-input">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>
                  <FaLock /> New Password
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>

              <div className="form-group">
                <label>
                  <FaLock /> Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
    </>
  );
};

export default Settings;